import { useCallback, useContext } from "react";

import { deleteFile as fileApiDelete } from "services/fileApi/methods/deleteFile";
import useGetAccessToken from "components/Login/useGetAccessToken";
import { useUploadProgress } from "./useUploadProgress";
import { uploadFiles } from "../utilities/uploadFiles";
import { RemoveFileMessageContext } from "../UploadContext";

interface UseUploadFileArgs {
  threadId: string;
}

/**
 * Hook wrapper around `../utilities/uploadFiles.ts`.
 *
 * Callers pass the active thread's id explicitly.
 */
export function useUploadFile({ threadId }: UseUploadFileArgs) {
  const removeMessagesAfterStream = useContext(RemoveFileMessageContext);
  if (removeMessagesAfterStream === null) {
    throw new Error("useUploadFile must be used within an UploadProvider");
  }
  const { setUploadProgress, removeUploadProgress } = useUploadProgress();
  const getAccessToken = useGetAccessToken();

  const handleUpload = useCallback(
    async (files: File[]) => {
      return await uploadFiles({
        files,
        chatId: threadId,
        getAccessToken,
        setUploadProgress,
        removeUploadProgress,
      });
    },
    [threadId, getAccessToken, setUploadProgress, removeUploadProgress]
  );

  // Delete an uploaded file from the chat thread.
  const handleDelete = useCallback(
    async (messageUuid: string, fileId?: string | null) => {
      // Ask the user to confirm deletion.
      const confirmDelete = window.confirm(
        "Are you sure you want to delete this file?"
      );
      if (!confirmDelete) return;

      if (fileId) {
        await fileApiDelete(fileId, getAccessToken);
      }

      // Saves nothing if the message is already gone, for example because the
      // file was deleted from the file explorer pane.
      await removeMessagesAfterStream(
        threadId,
        (message) => message.role === "file" && message.uuid === messageUuid
      );
    },
    [threadId, getAccessToken, removeMessagesAfterStream]
  );

  return {
    handleUpload,
    handleDelete,
  };
}
